// "Tulips": a soft pink spring morning, shoots rising → tap or swipe to bloom a field → it gathers into one bunch, held out → the painting.
const H = (i) => {
  const x = Math.sin(i * 127.1 + 3.7) * 43758.5453;
  return x - Math.floor(x);
};
// maroon, orange, yellow and a few blues, as she saw them; back rows first so the front overlaps
const TULIPS = Array.from({ length: 30 }, (_, k) => {
  const row = 2 - Math.floor(k / 10);
  const i = k % 10;
  const th = -Math.PI + ((i + 0.5 + row * 0.33) / 10) * 2 * Math.PI + (H(k) - 0.5) * 0.25;
  return { th: Math.atan2(Math.sin(th), Math.cos(th)), row, h: H(k + 40), at: 7 + H(k + 80) * 7 };
});

export const tulips = {
  slug: "tulips",
  duration: 28,
  tulips: TULIPS,
  captionTail: "I never could keep them growing, so I painted them to stay.",
  stages: [
    { id: "spring", at: 0, hint: "an early spring morning" },
    { id: "bloom", at: 6, hint: "tap or swipe to bloom" },
    { id: "bunch", at: 15, hint: "for you" },
    { id: "settle", at: 21.5, hint: "" },
  ],
  line: { text: "To be surrounded by flowers is the highest compliment the universe can pay you.", from: 14, to: 27.5, pos: "low" },
  touch: { kind: "tulip", from: 5.5, until: 15 },
  // a soft pink spring morning over the tulip field, mist lifting as they bloom
  layers: [
    { src: "/arcs/tulips/field.webp", wrap: 3.48, th: 0, h: 8.4, y: 1.8, r: 4.9, mist: [[0, 0.35], [8, 0.15]], mistCol: "#f6d9e2", opacity: [[0, 1], [5.5, 0.62], [15, 0.62], [16.5, 1]], show: [-1, 0, 99, 99] },
  ],
  ambient: { scene: "summer", until: 99, then: [] },
  look: { start: -0.08, lift: [15, 18], end: 0.0, yaw: [[0, 0], [7, 0], [10, 0.6], [13, -0.6], [15.5, 0], [28, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "tulips", pos: "low" },
  speed: [[0, 1], [28, 1]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uTul[30];
uniform float uOpen[30];
varying vec3 vPos;
${"__NOISE__"}
float hash(float n){ return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float seg(vec2 p, vec2 a, vec2 b){ vec2 pa = p - a, ba = b - a; float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0); return length(pa - ba * h); }
float ink(float d, float w){ return 1.0 - smoothstep(w * 0.6, w, d); }
vec3 tulipCol(float h){
  return h < 0.3 ? vec3(0.47, 0.08, 0.17) : h < 0.56 ? vec3(0.96, 0.46, 0.14) : h < 0.84 ? vec3(0.99, 0.8, 0.24) : vec3(0.3, 0.42, 0.84);
}
// a tulip cup seen side-on, p in head units: a rounded base, splaying sides, three petal tips that part as it opens
vec4 cup(vec2 p, float open, vec3 c){
  float w = mix(0.36, 0.6, open);
  float inside;
  if (p.y < 0.0) inside = step(abs(p.x), w * sqrt(max(0.0, 1.0 - pow(p.y / 0.55, 2.0))));
  else {
    float ww = w * (1.0 + 0.35 * open * p.y);
    float top = 0.55 - (0.12 + 0.3 * open) * (1.0 - abs(cos(p.x / ww * 4.712)));
    inside = step(abs(p.x), ww) * step(p.y, top);
  }
  float lines = 1.0 - 0.18 * ink(abs(abs(p.x) - w * 0.38), 0.05) * open;
  vec3 col = c * (0.72 + 0.38 * smoothstep(-0.55, 0.5, p.y)) * lines;
  return vec4(col, inside);
}
vec2 headPos(int i, float grow, float g, out vec2 base, out float s){
  vec4 tl = uTul[i];
  float row = tl.y;
  float sc = 1.0 - row * 0.22;
  float fx = tl.x * uRadius;
  float y0 = row * 0.32;
  vec2 headF = vec2(fx, y0 + (0.18 + (0.7 + row * 0.42) * sc * grow));
  vec2 headB = vec2((tl.z - 0.5) * 1.15, 1.5 + fract(tl.z * 7.3) * 0.6);
  base = mix(vec2(fx, y0), vec2(0.0, 0.3), g);
  s = mix(sc * 0.3, 0.19, g) * smoothstep(0.2, 1.0, grow);
  vec2 h = mix(headF, headB, g);
  h.x += sin(uT * 1.2 + tl.z * 20.0) * 0.025 * (1.0 + 2.0 * g);
  return h;
}
void main(){
  float n = snoise(vPos * 0.7) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.2 + 1.1) * 0.5 + 0.5;
  float grow = smoothstep(0.8, 6.0, uT);
  float g = smoothstep(15.0, 20.0, uT);
  float ground = smoothstep(16.0, 21.0, uT);
  float settle = smoothstep(21.5, 26.5, uT);
  vec3 pinkGround = vec3(0.84, 0.5, 0.5) * (0.85 + 0.3 * (snoise(vec3(vPos.x * 2.0, vPos.y * 6.0, vPos.z * 2.0)) * 0.5 + 0.5));
  vec3 col;
  vec2 local;
  if (uSurface < 0.5) {
    float th = atan(vPos.x, -vPos.z);
    vec2 q = vec2(th * uRadius, vPos.y);
    col = mix(vec3(0.99, 0.88, 0.87), vec3(0.95, 0.75, 0.82), smoothstep(0.0, 8.0, q.y)) + 0.02 * n;
    col += vec3(1.0, 0.92, 0.8) * exp(-length(vec2(q.x - 2.0, q.y - 4.0)) * 0.4) * 0.12;
    col = mix(col, mix(vec3(0.7, 0.5, 0.45), vec3(0.6, 0.42, 0.4), n), (1.0 - smoothstep(0.15, 0.4, q.y)) * (1.0 - g));
    col = mix(col, pinkGround, ground);
    // stems and leaves, gathering through the tie of the bunch
    for (int i = 0; i < 30; i++){
      vec2 base; float s;
      vec2 head = headPos(i, grow, g, base, s);
      // the field stems fade as the flowers gather; in the bunch every stem runs straight to the tie
      float vis = mix(1.0, 1.0 - smoothstep(1.2, 2.2, abs(head.x)), smoothstep(0.0, 0.15, g) * (1.0 - smoothstep(0.85, 1.0, g)));
      float sw = 0.03 * (1.0 - uTul[i].y * 0.2);
      vec3 green = mix(vec3(0.24, 0.46, 0.2), vec3(0.32, 0.56, 0.24), n2);
      col = mix(col, green, ink(seg(q, base, head), sw) * step(0.01, grow) * (1.0 - smoothstep(0.0, 0.25, g)));
      col = mix(col, green, ink(min(seg(q, head, vec2(0.0, 0.75)), seg(q, vec2(0.0, 0.75), vec2(0.0, 0.3))), sw) * smoothstep(0.2, 0.5, g) * vis);
      vec2 lp = q - vec2(uTul[i].x * uRadius, uTul[i].y * 0.32) - vec2(0.06, 0.0);
      float blade = step(0.0, lp.y) * step(abs(lp.x - lp.y * 0.25), 0.07 * (1.0 - lp.y / (0.6 * grow + 0.01))) * step(lp.y, 0.6 * grow);
      col = mix(col, vec3(0.28, 0.5, 0.22), blade * (1.0 - g));
    }
    // the bunch, held out to you: a paper wrap and a ribbon
    float wy = q.y - 0.3;
    float wrap = step(0.0, wy) * step(wy, 0.8) * step(abs(q.x), 0.06 + 0.32 * wy / 0.8);
    col = mix(col, mix(vec3(0.97, 0.92, 0.85), vec3(0.9, 0.84, 0.78), n2), wrap * g);
    col = mix(col, vec3(0.6, 0.2, 0.36), ink(abs(q.y - 0.72), 0.035) * step(abs(q.x), 0.3) * g);
    // the heads: green buds, then cups opening one by one
    for (int i = 0; i < 30; i++){
      vec2 base; float s;
      vec2 head = headPos(i, grow, g, base, s);
      if (s <= 0.0) continue;
      float open = smoothstep(uOpen[i], uOpen[i] + 1.2, uT);
      vec3 c = mix(vec3(0.42, 0.6, 0.3), tulipCol(uTul[i].z), smoothstep(uOpen[i] - 1.4, uOpen[i], uT));
      vec4 k = cup((q - head) / s, open, c);
      float vis = mix(1.0, 1.0 - smoothstep(1.2, 2.2, abs(head.x)), smoothstep(0.0, 0.15, g) * (1.0 - smoothstep(0.85, 1.0, g)));
      col = mix(col, k.rgb, k.a * vis);
    }
    // a light breeze carrying a few petals (petal shapes)
    for (int i = 0; i < 8; i++){
      float fi = float(i);
      float x = mod(hash(fi) * 30.0 + uT * 0.7, 6.2832 * uRadius) - 3.1416 * uRadius;
      vec2 pp = q - vec2(x, 1.0 + hash(fi + 3.0) * 2.4 + sin(uT * 0.9 + fi) * 0.3);
      float a = uT * 0.8 + fi;
      pp = mat2(cos(a), -sin(a), sin(a), cos(a)) * pp;
      float petal = 1.0 - smoothstep(0.9, 1.0, length(vec2(pp.x / (0.045 * (1.0 - clamp(pp.y / 0.1, -1.0, 1.0) * 0.6)), pp.y / 0.09)));
      col = mix(col, tulipCol(hash(fi + 9.0)) * 1.05, petal * g * (1.0 - ground * 0.5));
    }
    local = vec2(q.x, q.y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz;
    col = mix(vec3(0.62, 0.46, 0.42), vec3(0.72, 0.55, 0.5), n);
    // from above: shoots, then open tulips all round
    vec2 cell = floor(xz / 0.6);
    float h = hash2(cell);
    vec2 c = (cell + 0.5 + 0.3 * vec2(hash2(cell + 2.0) - 0.5, hash2(cell + 5.0) - 0.5)) * 0.6;
    vec2 p = xz - c;
    float r = length(p);
    float open = smoothstep(7.0 + h * 7.0, 8.2 + h * 7.0, uT);
    float a = atan(p.y, p.x) + h * 6.0;
    float pr = 0.04 + 0.1 * open * (0.3 + 0.7 * pow(abs(cos(a * 1.5)), 0.6));
    float shoot = ink(r, 0.05 * grow) * step(0.35, h);
    col = mix(col, vec3(0.3, 0.52, 0.24), shoot * (1.0 - open));
    col = mix(col, tulipCol(fract(h * 13.0)) * (0.75 + 0.3 * smoothstep(0.0, pr, r)), (1.0 - smoothstep(pr * 0.85, pr, r)) * step(0.35, h) * open);
    col = mix(col, pinkGround, max(ground, g * 0.7));
    local = xz;
  } else {
    col = mix(vec3(0.97, 0.82, 0.85), vec3(0.93, 0.72, 0.8), n);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  float m = smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25);
  col = mix(col, paint, m);
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
